import React, { FunctionComponent, useCallback, useEffect, useMemo } from 'react';

import { useSelector } from 'react-redux';

import I18N from '../../../../../../shared/components/i18n';
import { OrbModalConfirm } from '../../../../../../shared/components/orb-modal-confirm';
import { SettingsSectionKey } from '../../../../../../shared/features/settings-modal/constants';
import { openSettingsModalAction } from '../../../../../../shared/features/settings-modal/slice';
import { isMobileUa } from '../../../../../../shared/features/uatraits';
import { i18n } from '../../../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../../../shared/helpers/metrika';
import { useActions } from '../../../../../../shared/hooks';
import { useTypedSelector } from '../../../../../../shared/hooks/useTypedSelector';
import { selectHasSummarizationSettingsPermission } from '../../../permissions';
import { selectStopDialogContentKey } from '../../selectors';
import { SummarizationModalProps } from '../types';
import { ADD_HEADER_CONTENT_KEYS, CONTENT_LIST_KEYS } from './constants';
import css from './styles.css';

export const SummarizationStopModal: FunctionComponent<SummarizationModalProps> = ({ onClose: closeHandler }) => {
    const onConfirm = () =>
        closeHandler?.({
            isConfirmed: true,
        });
    const onCancel = () => closeHandler?.({ isConfirmed: false });

    const actions = useActions({
        openSettingsModalAction,
    });

    const onSettingsClick = useCallback(() => {
        countMetrika('meeting_screen', 'summarization', 'summarization_settings_click_from_popup');
        actions.openSettingsModalAction({
            sectionKey: SettingsSectionKey.Permissions,
        });
    }, [actions]);

    const stopDialogContentKey = useSelector(selectStopDialogContentKey);
    const hasSummarizationSettingsPermission = useSelector(selectHasSummarizationSettingsPermission);
    const appLang = useTypedSelector((state) => state.config.appLang);
    const isMobile = useSelector(isMobileUa);

    const isFooterVisible = useMemo(
        () => hasSummarizationSettingsPermission && !isMobile,
        [hasSummarizationSettingsPermission, isMobile]
    );
    useEffect(() => {
        if (isFooterVisible) {
            countMetrika('meeting_screen', 'summarization', 'stop_popup_with_sett_was_shown');
        } else {
            countMetrika('meeting_screen', 'summarization', 'stop_popup_without_sett_was_shown');
        }
    }, [isFooterVisible]);

    return (
        <OrbModalConfirm
            onClose={onCancel}
            onCancel={onCancel}
            onConfirm={onConfirm}
            title={i18n('summarization', 'stop-dialog-title-v2')}
            titleLineLimit={2}
            confirmLabel={i18n('summarization', 'stop-dialog-submit-v2')}
            cancelLabel={i18n('summarization', 'stop-dialog-deny-v2')}
            scope="inplace"
        >
            <div>{i18n('summarization', 'stop-dialog-content-v3')}</div>
            <br />
            {ADD_HEADER_CONTENT_KEYS.includes(stopDialogContentKey) && (
                <div className={css.receiveByMail}>{i18n('summarization', 'stop-dialog-content-receive-by-mail')}</div>
            )}
            <div className={css.dialogContent}>
                {i18n('summarization', stopDialogContentKey)}
                {(isFooterVisible || appLang === 'en') && !CONTENT_LIST_KEYS.includes(stopDialogContentKey) && '.'}
            </div>
            {isFooterVisible && (
                <>
                    <br />
                    <I18N
                        keyset="summarization"
                        loc="stop-dialog-change-participants-permissions"
                        className={css.changeParticipantsPermissions}
                    >
                        <span data-ref="link-settings" onClick={onSettingsClick} className={css.settings} />
                    </I18N>
                </>
            )}
        </OrbModalConfirm>
    );
};
